import { PLANBASE_CONFIG } from '../config';

/**
 * Get authentication headers for API requests
 * Uses user token if available, falls back to API key for public endpoints
 */
export function getAuthHeaders(includeUserToken = true): Record<string, string> {
  const token = localStorage.getItem('planbase_token');
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Project-ID': PLANBASE_CONFIG.projectId,
  };

  if (includeUserToken && token) {
    // Use user token for authenticated requests
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    // Use API key for public/unauthenticated requests
    headers['Authorization'] = `Bearer ${PLANBASE_CONFIG.apiKey}`;
  }

  return headers;
}

/**
 * Decodes a JWT token safely, handling base64url encoding
 */
export function decodeJWT(token: string): any {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;

    let base64Url = parts[1];

    // Add padding if missing
    while (base64Url.length % 4 !== 0) {
      base64Url += '=';
    }

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Get headers specifically for user-authenticated requests
 * Includes both Authorization and X-User-ID for compatibility
 */
export function getUserAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('planbase_token');
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Project-ID': PLANBASE_CONFIG.projectId,
    'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
  };
  
  // Extract user ID from JWT token and add as X-User-ID
  if (token) {
    try {
      const payload = decodeJWT(token);
      const userId = payload?.sub || payload?.user_id || payload?.id;
      if (userId) {
        headers['X-User-ID'] = userId;
      }
    } catch (error) {
      console.error('[getUserAuthHeaders] Failed to decode token:', error);
    }
  }
  
  return headers;
}

/**
 * Get headers for file upload requests
 */
export function getUploadHeaders(): Record<string, string> {
  const token = localStorage.getItem('planbase_token');
  
  return {
    'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
    'X-Project-ID': PLANBASE_CONFIG.projectId,
    ...(token ? { 'X-User-Token': token } : {}),
  };
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  const token = localStorage.getItem('planbase_token');
  const user = localStorage.getItem('planbase_user');
  return !!(token && user);
}

/**
 * Get current user from localStorage
 */
export function getCurrentUser() {
  const userStr = localStorage.getItem('planbase_user');
  if (!userStr) return null;
  
  try {
    return JSON.parse(userStr);
  } catch (error) {
    console.error('Failed to parse user data:', error);
    return null;
  }
}

/**
 * Clear authentication data
 */
export function clearAuth() {
  localStorage.removeItem('planbase_token');
  localStorage.removeItem('planbase_user');
}